import { API } from 'aws-amplify'
import { DeviceGroup } from './deviceGroups'
import { Zone } from './zones'

export type PlacementEntityType = 'device_group' | 'zone' | 'thing'

export type DeviceGroupEntity = Pick<DeviceGroup, 'name' | 'device_group_type' | 'status'>
export type ZoneEntity = Pick<Zone, 'name'>
export type ThingEntity = { name: string; thing_type_id: string }

export type PlacementEntity = DeviceGroupEntity | ZoneEntity | ThingEntity

export type SchematicPlacement = {
  placement_id: string
  site_schematic_id: string
  entity_type: PlacementEntityType
  entity_id: string
  /** Normalized 0..1 of the image width. */
  x: number
  /** Normalized 0..1 of the image height. */
  y: number
  entity: PlacementEntity
}

export type SiteSchematic = {
  site_schematic_id: string
  zone_id: string
  image_key: string
  image_width: number
  image_height: number
  content_type: string
  original_filename?: string | null
  /** Set when the source was a PDF, recording which page was rasterized. */
  source_page?: number | null
  created_at: string
  updated_at: string
  placements: SchematicPlacement[]
}

export type UpsertSiteSchematicPayload = {
  image_key: string
  image_width: number
  image_height: number
  content_type: string
  original_filename?: string
  source_page?: number
}

export type PlacementPayload = Pick<SchematicPlacement, 'entity_type' | 'entity_id' | 'x' | 'y'>

/**
 * Resolves to null when the site has no schematic yet, which is a 404 from the
 * API rather than an error state — the caller renders the upload prompt.
 */
export async function fetchSiteSchematic({ zoneId }: { zoneId: string }): Promise<SiteSchematic | null> {
  if (!zoneId) return Promise.reject('Zone ID is not defined')

  try {
    return await API.get('zones', `/zones/${zoneId}/schematic`, {})
  } catch (error: any) {
    if (error?.response?.status === 404) return null
    throw error
  }
}

export function upsertSiteSchematic(zoneId: string, body: UpsertSiteSchematicPayload): Promise<SiteSchematic> {
  return API.put('zones', `/zones/${zoneId}/schematic`, { body })
}

export function deleteSiteSchematic(zoneId: string): Promise<void> {
  return API.del('zones', `/zones/${zoneId}/schematic`, {})
}

export function saveSchematicPlacements(zoneId: string, placements: PlacementPayload[]): Promise<SiteSchematic> {
  return API.put('zones', `/zones/${zoneId}/schematic/placements`, { body: { placements } })
}
